import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { classNames } from '../../utils/classNames';
import UserProfileDropdown from './UserProfileDropdown';
import SecuritySettingsModal from '../common/SecuritySettingsModal';
import ProfileModal from '../common/ProfileModal';
import PlatformCredentialsModal from '../common/PlatformCredentialsModal';
import GlobalCacheSettingsModal from '../common/GlobalCacheSettingsModal';
import OrganizationSettingsModal from '../common/OrganizationSettingsModal';
import { usePreloadStore, useEnsurePreload } from '../../store/preloadStore';
import authService from '../../features/auth/services/authService';
import { ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';

interface NavbarProps {
  activeTab: string;
}

const Navbar: React.FC<NavbarProps> = ({ activeTab }) => {
  const navigate = useNavigate();
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showPlatformCredentialsModal, setShowPlatformCredentialsModal] = useState(false);
  const [showCacheSettingsModal, setShowCacheSettingsModal] = useState(false);
  const [showOrganizationSettingsModal, setShowOrganizationSettingsModal] = useState(false);
  
  // Use preloaded user data instead of separate API call
  useEnsurePreload();
  const { getUserInfo } = usePreloadStore();
  const userInfo = getUserInfo();
  const user = userInfo?.user || null;
  const has2FA = userInfo?.two_factor_enabled || false;
  
  // Check if user is authenticated
  const isAuthenticated = authService.isAuthenticated();

  // Define navigation tabs
  const navigationTabs = [
    { name: 'Autopilot', key: 'Autopilot', path: '/autopilot' },
    // { name: 'Copilot', key: 'Copilot', path: '/copilot' },
    // { name: 'Workbench', key: 'Workbench', path: '/workbench' },
  ];

  // Redirect to auth if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = async () => {
    try {
      await authService.logout();
      localStorage.removeItem('authToken');
      navigate('/auth');
    } catch (error) {
      
      // Force logout even if API call fails
      localStorage.removeItem('authToken');
      navigate('/auth');
    }
  };

  const handleOpenSecuritySettings = () => {
    setShowSecurityModal(true);
  };

  const handleOpenProfile = () => {
    setShowProfileModal(true);
  };

  const handleOpenPlatformCredentials = () => {
    setShowPlatformCredentialsModal(true);
  };

  const handleOpenCacheSettings = () => {
    setShowCacheSettingsModal(true);
  };

  const handleOpenOrganizationSettings = () => {
    setShowOrganizationSettingsModal(true);
  };

  if (!isAuthenticated) {
    return (
      <nav className="bg-white/10 dark:bg-black/20 backdrop-blur-sm px-4 py-2.5 shadow-sm transition-colors duration-200">
        <div className="flex flex-wrap justify-between items-center">
          <div className="flex items-center">
            <img 
              src="/pscx_logo.png" 
              alt="Company Logo" 
              className="w-auto h-8 invert transition-opacity duration-200 hover:opacity-90 dark:invert-0" 
            />
          </div>
          <div className="flex items-center space-x-6">
            <div className="w-32 h-8 rounded-md animate-pulse bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary"></div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="bg-white/10 dark:bg-black/20 backdrop-blur-sm px-4 py-2.5 shadow-sm transition-colors duration-200">
      <div className="flex flex-wrap justify-between items-center">
        {/* Logo on the left */}
        <div className="flex items-center">
          <img 
            src="/pscx_logo.png" 
            alt="Company Logo" 
            className="w-auto h-8 invert transition-opacity duration-200 hover:opacity-90 dark:invert-0" 
          />
        </div>
        
        {/* Navigation tabs, ThemeToggle, and Logout Button on the right */}
        <div className="flex items-center space-x-6">
          {/* Navigation tabs */}
          <div className="flex space-x-4">
            {navigationTabs.map((tab) => (
              <Link
                key={tab.key}
                to={tab.path}
                className={classNames(
                  activeTab === tab.key
                    ? 'border-b-2 border-primary-light bg-theme-bg-secondary text-primary-light font-semibold dark:border-primary-dark dark:bg-dark-theme-bg-secondary dark:text-primary-dark'
                    : 'border-transparent text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-bg-secondary dark:text-dark-theme-text-secondary dark:hover:text-dark-theme-text-primary dark:hover:bg-dark-theme-bg-secondary',
                  'px-4 py-2 text-sm rounded-md transition-colors duration-200'
                )}
              >
                {tab.name}
              </Link>
            ))}
          </div>
          
          {/* ThemeToggle removed - now in BackgroundControls */}

          {/* User Profile Dropdown */}
          {user && (
            <UserProfileDropdown
              user={user}
              has2FA={has2FA}
              onLogout={handleLogout}
              onOpenSecuritySettings={handleOpenSecuritySettings}
              onOpenProfile={handleOpenProfile}
              onOpenPlatformCredentials={handleOpenPlatformCredentials}
              onOpenCacheSettings={handleOpenCacheSettings}
              onOpenOrganizationSettings={handleOpenOrganizationSettings}
            />
          )}
        </div>
      </div>

      {/* Security Settings Modal */}
      <SecuritySettingsModal
        isOpen={showSecurityModal}
        onClose={() => setShowSecurityModal(false)}
      />

      {/* Profile Modal */}
      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />

      {/* Platform Credentials Modal */}
      <PlatformCredentialsModal
        isOpen={showPlatformCredentialsModal}
        onClose={() => setShowPlatformCredentialsModal(false)}
      />

      {/* Cache Settings Modal */}
      <GlobalCacheSettingsModal
        isOpen={showCacheSettingsModal}
        onClose={() => setShowCacheSettingsModal(false)}
      />

      {/* Organization Settings Modal */}
      <OrganizationSettingsModal
        isOpen={showOrganizationSettingsModal}
        onClose={() => setShowOrganizationSettingsModal(false)}
      />
    </nav>
  );
};

export default Navbar;
